import endpoints from "@config/endpoints"
import { selectUserId, selectUserSettings } from "@selectors/user"
import { setCurrentTimezone } from "@utils/dateUtils"

import api from "../api"
import { store } from "../index"

import actionTypes from "./actionTypes"
import { updateProfile } from "./userActions"

const postSettings = settings => ({
  type: actionTypes.POST_SETTINGS,
  payload: settings,
})

const resyncCalendarTeeupEvents = () => ({
  type: actionTypes.CALENDAR_RESYNC_TEEUP_EVENTS,
})

export const postUserSettings = async settings => {
  let url = endpoints.settings

  // WN1: customTimezone is tracked separately (changeUserTimeZone -> PATCH users/me,
  // below), never through this generic settings endpoint. The previous code did
  // `delete settings.calendarSync.customTimezone`, which MUTATED the caller's object
  // in place -- and that object is frequently the very same reference held in Redux
  // state (selectUserSettings returns state.user.settings directly, and changeSettings
  // spreads it shallowly, so nested objects like calendarSync keep their original
  // reference). That silently stripped customTimezone out of live app state on every
  // settings write, before this function's own SET_SETTINGS dispatch could even run.
  // Build the outgoing request body as a copy instead, so the caller's object (and
  // Redux state) is never touched.
  let body = settings
  if (settings?.calendarSync?.customTimezone !== undefined) {
    body = { ...settings, calendarSync: { ...settings.calendarSync } }
    delete body.calendarSync.customTimezone
  }

  // WN1: the dev backend (and the devBackendShim route match for
  // "users/settings") only accepts PUT/PATCH on this resource -- a POST
  // silently missed the shim's route match and 404/405'd against dev,
  // so the .catch below swallowed the error and SET_SETTINGS never fired
  // (the toggle looked inert -- click handler ran, network call failed
  // silently, store never updated).
  return api.client
    .put(url, body)
    .then(response => {
      const { data } = response
      // The dev backend's PUT /users/me/settings returns {settings: {...}}
      // (an enveloped shape), not the flat settings object the beta API
      // returns. Unwrap it so SET_SETTINGS merges the real keys into
      // state.settings instead of nesting them one level deeper under a
      // stray "settings" key that no selector reads.
      const nextSettings = data && data.settings ? data.settings : data
      // The dev backend's settings envelope never round-trips calendarSync.customTimezone
      // (by design, it is tracked outside this endpoint) -- preserve whatever the caller
      // already had locally so App.js's "settings.calendarSync owns customTimezone"
      // app-gate never regresses to undefined and strands the app on the splash screen.
      const mergedSettings = {
        ...nextSettings,
        calendarSync: {
          ...(settings.calendarSync || {}),
          ...((nextSettings && nextSettings.calendarSync) || {}),
        },
      }
      store.dispatch({
        type: actionTypes.SET_SETTINGS,
        payload: { ...mergedSettings },
      })

      return mergedSettings
    })
    .catch(error => {
      // wave4-fix17: surface the failure -- and by the interceptor guard above it can no longer
      // cascade into a logout. Session always survives a failed settings write.
      // eslint-disable-next-line no-console
      console.log("post user settings error", error)
      try {
        // zustand store is callable outside React
        // eslint-disable-next-line global-require
        const { useSnackbarStore } = require("stores/snackbarStore")
        useSnackbarStore.getState().setSnackbarConfig({
          message: "Could not save settings. Please try again.",
          open: true,
        })
      } catch (e) {
        /* snackbar unavailable -- swallow */
      }
    })
}

export const changeUserTimeZone = timezone => {
  updateProfile({ timezone: timezone === null ? "" : timezone })
    .then(response => {
      store.dispatch({
        type: actionTypes.CHANGE_TIMEZONE,
        payload: { customTimezone: timezone },
      })
      store.dispatch({
        type: actionTypes.UPDATE_USER_INFO,
        payload: { ...response, timezone },
      })
      setCurrentTimezone(timezone)
    })
    .catch(error => {
      console.log("PATCH changeUserTimeZone error", error)
    })
}

export const changeSettings = async settings => {
  const state = store.getState()
  const userId = selectUserId(state)
  const userSettings = selectUserSettings(state)
  // if (userSettings.sounds) {
  //     delete userSettings.sounds
  // }
  const fullSettings = {
    ...userSettings,
    ...settings,
  }
  if (userId) {
    await postUserSettings(fullSettings, settings.callback)
    if (settings.calendarSync?.customTimezone) {
      const timezone = fullSettings.calendarSync.customTimezone
      changeUserTimeZone(timezone)
    }
  }
}

export { postSettings, resyncCalendarTeeupEvents }
